import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { wishlistService } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { getProductImageUrl } from '../../utils/imageHelper';
import NotificationContainer from '../../components/Notification/NotificationContainer';
import styles from './Wishlist.module.css';

const Wishlist = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all'
  });

  // Rediriger si non authentifié
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les favoris
  useEffect(() => {
    if (isAuthenticated) {
      loadWishlist();
    }
  }, [isAuthenticated]);

  const loadWishlist = async () => {
    try {
      setLoading(true);
      const response = await wishlistService.getWishlist();

      if (response.success && response.data) {
        setWishlistItems(Array.isArray(response.data) ? response.data : []);
      } else {
        setWishlistItems([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
      addNotification('Erreur lors du chargement des favoris', 'error');
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les favoris
  const filteredItems = wishlistItems.filter(item => {
    if (!item.product) return false;

    const product = item.product;
    const searchMatch = !filters.search ||
      product.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      product.description?.toLowerCase().includes(filters.search.toLowerCase());

    const categoryMatch = filters.category === 'all' ||
      product.category_id?.toString() === filters.category;

    return searchMatch && categoryMatch;
  });

  // Retirer des favoris
  const handleRemoveFromWishlist = async (productId) => {
    try {
      const response = await wishlistService.removeFromWishlist(productId);
      if (response.success) {
        addNotification('Produit retiré des favoris', 'success');
        await loadWishlist();
      } else {
        addNotification(response.message || 'Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      addNotification('Erreur lors de la suppression des favoris', 'error');
    }
  };

  // Ajouter au panier
  const handleAddToCart = async (product) => {
    try {
      const result = await addToCart(product.id, 1);
      if (result.success) {
        addNotification('Produit ajouté au panier', 'success');
      } else {
        addNotification(result.error || 'Erreur lors de l\'ajout au panier', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      addNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };


  // Fonction pour formater les prix
  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0';
    return numPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Gérer le changement de filtre
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (authLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={styles.container}>
      <NotificationContainer notifications={notifications} removeNotification={removeNotification} />

      <div className={styles.header}>
        <h1>Mes Favoris</h1>
        <p className={styles.subtitle}>Consultez vos produits favoris</p>
      </div>

      {/* Filtres */}
      {wishlistItems.length > 0 && (
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label htmlFor="search">Rechercher</label>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Nom du produit..."
              value={filters.search}
              onChange={handleFilterChange}
              className={styles.filterInput}
            />
          </div>
        </div>
      )}

      {/* Liste des favoris */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Chargement de vos favoris...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          <svg className={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <h3>
            {wishlistItems.length === 0
              ? 'Aucun produit dans vos favoris'
              : 'Aucun produit ne correspond à vos critères'}
          </h3>
          <p>
            {wishlistItems.length === 0
              ? 'Commencez à ajouter des produits à vos favoris pour les retrouver facilement.'
              : 'Essayez de modifier vos critères de recherche.'}
          </p>
          {wishlistItems.length === 0 && (
            <button
              className={styles.shopButton}
              onClick={() => navigate('/')}
            >
              Découvrir les produits
            </button>
          )}
        </div>
      ) : (
        <div className={styles.productsGrid}>
          {filteredItems.map((item) => {
            const product = item.product;
            if (!product) return null;

            return (
              <div key={item.id || product.id} className={styles.productCard}>
                <div className={styles.productImageContainer}>
                  <Link to={`/product/${product.id}`}>
                    <img
                      src={product.primary_image ? getProductImageUrl(product.primary_image) : (product.images?.[0] ? getProductImageUrl(product.images[0]) : '/images/leaves-right.png')}
                      alt={product.name}
                      className={styles.productImage}
                      onError={(e) => {
                        e.target.src = '/images/leaves-right.png';
                      }}
                    />
                  </Link>
                  <button
                    className={styles.removeButton}
                    onClick={() => handleRemoveFromWishlist(product.id)}
                    title="Retirer des favoris"
                  >
                    <svg className={styles.heartIcon} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                <div className={styles.productInfo}>
                  <Link to={`/product/${product.id}`} className={styles.productLink}>
                    <h3 className={styles.productName}>{product.name}</h3>
                  </Link>

                  {product.category && (
                    <p className={styles.productCategory}>{product.category.name}</p>
                  )}

                  <div className={styles.productPrice}>
                    {formatPrice(product.price)} FCFA
                    {product.unit && (
                      <span className={styles.productUnit}> / {product.unit}</span>
                    )}
                  </div>

                  {product.stock_quantity !== undefined && (
                    <p className={styles.productStock}>
                      {product.stock_quantity > 0 ? (
                        <span className={styles.inStock}>En stock ({product.stock_quantity})</span>
                      ) : (
                        <span className={styles.outOfStock}>Rupture de stock</span>
                      )}
                    </p>
                  )}

                  {product.supplier && (
                    <p className={styles.productSupplier}>
                      Fournisseur: {product.supplier.business_name || product.supplier.contact_person}
                    </p>
                  )}
                </div>

                <div className={styles.productActions}>
                  <button
                    className={styles.addToCartButton}
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.stock_quantity || product.stock_quantity === 0}
                  >
                    <svg className={styles.cartIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Ajouter au panier
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Wishlist;



